import React, { useMemo, useRef, useState } from 'react'
import { Badge, Text } from '@fluentui/react-components'
import './ArchitectureDiagram.css'
import type { ArchEdge, ArchNode } from './content'
import { architectureContent } from './content'
import { useEffects } from '../../contexts/EffectsContext'
import { useSkillsFilter } from '../../contexts/SkillsFilterContext'
import { useMobileDetection } from '../../hooks/useMobileDetection'

const NODE_W = 150
const NODE_H = 46
/** On small screens the whole SVG shrinks, so nodes get a size boost */
const MOBILE_SCALE = 1.3

/**
 * Interactive SVG map of the M365 stack. Animated "packets" travel along
 * the connections (gated by the data-flow effect flag); hovering or
 * tapping a node highlights its connections and shows details below.
 */
export const ArchitectureDiagram: React.FC = () => {
	const { effects } = useEffects()
	const { setCategory, setMatrixAutoOpen } = useSkillsFilter()
	const isMobile = useMobileDetection()
	const sizeScale = isMobile ? MOBILE_SCALE : 1
	const [activeId, setActiveId] = useState<string | null>(null)
	const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const { nodes, edges } = architectureContent

	// Selecting a node pre-filters the skills matrix below and opens the
	// section temporarily — without scrolling or stealing focus.
	const selectNode = (node: ArchNode) => {
		setActiveId(node.id)
		if (node.skillsCategory) {
			setCategory(node.skillsCategory)
		}
		if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
		setMatrixAutoOpen(true)
	}

	// Small delay before releasing the auto-open, so moving the mouse
	// between nodes doesn't flap the section open/closed.
	const releaseNode = () => {
		if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
		closeTimerRef.current = setTimeout(() => setMatrixAutoOpen(false), 300)
	}

	const nodeById = useMemo(() => {
		const map = new Map<string, ArchNode>()
		nodes.forEach(node => map.set(node.id, node))
		return map
	}, [nodes])

	const pathFor = (edge: ArchEdge): string => {
		if (edge.d) return edge.d
		const from = nodeById.get(edge.from)
		const to = nodeById.get(edge.to)
		if (!from || !to) return ''
		const mx = (from.x + to.x) / 2
		const my = (from.y + to.y) / 2
		// perpendicular offset for a gentle curve
		const dx = to.x - from.x
		const dy = to.y - from.y
		const len = Math.sqrt(dx * dx + dy * dy) || 1
		const bend = edge.bend ?? 0
		const cx = mx + (-dy / len) * bend
		const cy = my + (dx / len) * bend
		return `M ${from.x} ${from.y} Q ${cx} ${cy} ${to.x} ${to.y}`
	}

	const isEdgeActive = (edge: ArchEdge): boolean =>
		activeId !== null && (edge.from === activeId || edge.to === activeId)

	const activeNode = activeId ? nodeById.get(activeId) : undefined

	return (
		<div className="arch-diagram">
			<svg
				viewBox="0 0 720 530"
				role="img"
				aria-label="Architecture diagram: SPFx web parts and Teams apps connect through Microsoft Graph to SharePoint Online, AKS, Azure Functions, Entra ID and internal and external APIs"
			>
				{/* edges */}
				{edges.map(edge => (
					<path
						key={edge.id}
						id={`arch-${edge.id}`}
						className={`arch-edge${edge.kind === 'auth' ? ' arch-edge-auth' : ''}${isEdgeActive(edge) ? ' arch-edge-active' : ''}${activeId && !isEdgeActive(edge) ? ' arch-edge-dimmed' : ''}`}
						d={pathFor(edge)}
					/>
				))}

				{/* animated packets — the data-flow effect (data edges only) */}
				{effects.dataFlow && edges.filter(edge => edge.kind !== 'auth').map((edge, index) => {
					const color = nodeById.get(edge.to)?.color ?? '#61dafb'
					const reverseColor = nodeById.get(edge.from)?.color ?? '#61dafb'
					// Packets on routes unrelated to the selected node calm
					// down: dimmer, smaller, slower.
					const dimmed = activeId !== null && !isEdgeActive(edge)
					const slow = dimmed ? 1.8 : 1
					const packetClass = `arch-packet${dimmed ? ' arch-packet-dim' : ''}`
					const forwardDur = (1.5 + (index % 3) * 0.25) * slow
					const reverseDur = (1.8 + (index % 4) * 0.25) * slow
					return (
						<g key={`packets-${edge.id}`}>
							{/* two packets per direction, offset by half a cycle */}
							{[0, forwardDur / 2].map((offset, packetIndex) => (
								<circle key={`f-${packetIndex}`} r={dimmed ? 2.5 : 4} fill={color} className={packetClass}>
									<animateMotion
										dur={`${forwardDur}s`}
										begin={`${index * 0.2 + offset}s`}
										repeatCount="indefinite"
									>
										<mpath href={`#arch-${edge.id}`} />
									</animateMotion>
								</circle>
							))}
							{[0, reverseDur / 2].map((offset, packetIndex) => (
								<circle key={`r-${packetIndex}`} r={dimmed ? 2 : 3} fill={reverseColor} className={packetClass}>
									<animateMotion
										dur={`${reverseDur}s`}
										begin={`${0.3 + index * 0.25 + offset}s`}
										repeatCount="indefinite"
										calcMode="linear"
										keyPoints="1;0"
										keyTimes="0;1"
									>
										<mpath href={`#arch-${edge.id}`} />
									</animateMotion>
								</circle>
							))}
						</g>
					)
				})}

				{/* key-shaped packets on identity edges — tokens flowing out of Entra */}
				{effects.dataFlow && edges.filter(edge => edge.kind === 'auth').map((edge, index) => {
					const dimmed = activeId !== null && !isEdgeActive(edge)
					const dur = (3.6 + (index % 3) * 0.5) * (dimmed ? 1.8 : 1)
					return (
						<g key={`keys-${edge.id}`}>
							{[0, dur / 2].map((offset, keyIndex) => (
								<g key={keyIndex} className={`arch-key${dimmed ? ' arch-packet-dim' : ''}`}>
									{/* simple key: ring + shaft + two teeth */}
									<circle cx="-4" cy="0" r="2.5" />
									<line x1="-1.5" y1="0" x2="6" y2="0" />
									<line x1="3" y1="0" x2="3" y2="2.5" />
									<line x1="5.5" y1="0" x2="5.5" y2="2.5" />
									<animateMotion
										dur={`${dur}s`}
										begin={`${index * 0.7 + offset}s`}
										repeatCount="indefinite"
										rotate="auto"
									>
										<mpath href={`#arch-${edge.id}`} />
									</animateMotion>
								</g>
							))}
						</g>
					)
				})}

				{/* nodes */}
				{nodes.map(node => (
					<g
						key={node.id}
						className={`arch-node${activeId === node.id ? ' arch-node-active' : ''}`}
						style={{ '--node-color': node.color } as React.CSSProperties}
						tabIndex={0}
						role="button"
						aria-label={`${node.label}: ${node.description}`}
						onMouseEnter={() => selectNode(node)}
						onMouseLeave={releaseNode}
						onFocus={() => selectNode(node)}
						onBlur={releaseNode}
						onClick={() => selectNode(node)}
					>
						<rect
							x={node.x - ((node.w ?? NODE_W) * sizeScale) / 2}
							y={node.y - (NODE_H * sizeScale) / 2}
							width={(node.w ?? NODE_W) * sizeScale}
							height={NODE_H * sizeScale}
							rx={10}
						/>
						<text
							x={node.x}
							y={node.y + 5 * sizeScale}
							textAnchor="middle"
							fontSize={14 * sizeScale}
						>
							{node.label}
						</text>
					</g>
				))}
			</svg>

			<div className="arch-legend" aria-hidden="true">
				<span className="arch-legend-item">
					<svg width="28" height="8" viewBox="0 0 28 8"><line x1="0" y1="4" x2="28" y2="4" className="arch-legend-line-data" /></svg>
					data flow
				</span>
				<span className="arch-legend-item">
					<svg width="28" height="8" viewBox="0 0 28 8"><line x1="0" y1="4" x2="28" y2="4" className="arch-legend-line-auth" /></svg>
					identity / auth
				</span>
			</div>

			<div className="arch-info" aria-live="polite">
				{activeNode ? (
					<>
						<Text weight="semibold" size={400} block style={{ color: activeNode.color }}>
							{activeNode.label}
						</Text>
						<Text size={300} block>
							{activeNode.description}
						</Text>
						<div className="arch-info-tags">
							{activeNode.tags.map(tag => (
								<Badge key={tag} color="informative" appearance="outline" size="small">
									{tag}
								</Badge>
							))}
						</div>
						{activeNode.skillsCategory && (
							<Text size={200} block className="arch-info-hint">
								↓ skills matrix below is filtered to “{activeNode.skillsCategory}”
							</Text>
						)}
					</>
				) : (
					<Text size={300} block className="arch-info-hint">
						Hover or tap a node to see what I build with it.
					</Text>
				)}
			</div>
		</div>
	)
}

export default ArchitectureDiagram
